import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { TenantContext, updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import { Redirect } from "react-router";
import Strings from "@my-dld/assets/strings";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { EjariApi } from "@eres/ejari-api";
import { EjariProperty, TenantUserProfile } from "@eres/ejari-api/Entity";
import { getEjariPropertyId, getPropertyId, setPropertyId } from "@eres/ejari-api/Utils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { ImperialSystem, MetricSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import { dispatch, navigateWithInParent } from "@my-dld/utils/StoreUtils";
import Actions from "@my-dld/actions";
import { VerifyTentUserProfile } from "@eres/ejari-api/IApi";
import * as SSO from "@10x/sso";
import PageContent from "@my-dld/components/PageContent";
import { resetTenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { EjariDetails } from "../../screens/PropertyDetails";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import { extractPropInfo } from "../PropertyHeader";
import Switch from "@my-dld/components/Switch";
import { getAppServices } from "@10x/coreapp";

interface Props extends RoutedViewProps {

}

interface State {
	unit: UnitSystem;
	showConfirmation?: boolean;
}

class PropertyDetails extends Component<Props, State> {
	static contextType = TenantContext;
	context!: React.ContextType<typeof TenantContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			unit: getAppServices().storeService.getStore().getState().User.unit,
		};
	}

	private static updateProperty( property: EjariProperty ) {
		const data = { property: property };
		updateTenantContext( data );
	}

	componentDidMount(): void {
		const { property, actionDone } = this.context;
		if ( actionDone ) {
			this.props.history.goBack();
		} else if ( property ) {
			trackEventWithData( "TenantEjariProperty", "View", {
				type: property.PropertyTypeId,
				id: property.PropertyId
			} );
		}
	}

	render() {
		const { property } = this.context;
		const { unit, showConfirmation } = this.state;
		if ( property == null )
			return <Redirect to={ "/mydld" }/>;
		let { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ Strings.property_details + " - " + lbl + " : " + val }
			titleExtra={ <Switch
				id={ "result-unit" }
				onLabel={ Strings.sq_mt }
				offLabel={ Strings.sq_ft }
				value={ this.state.unit.Id === MetricSystem.Id }
				onChange={ isSqm => {
					const update = { unit: ImperialSystem };
					if ( isSqm ) update.unit = MetricSystem;
					this.setState( update, () => {
						dispatch( Actions.UNIT_CHANGED, this.state.unit );
					} );
				} }
			/> }
			hideQLinks={ true }>
			<PageTitle title={ Strings.register_certificate } paths={ [ DashboardBCItem ] }/>
			<div className="tender-registration-form">
				<EjariDetails
					showRentalStatus={ true }
					property={ property }
					unit={ unit }/>
				{ !property!.IsRented && <div className="row">
					<div className="col-md-12">
						<div className="form-group">
							<button className="btn btn_1 btn_style1" type="submit" onClick={ this.onValidateProperty }>
								{ Strings.proceed }</button>
						</div>
					</div>
				</div> }
				{ showConfirmation && <ConfirmationPopup
					primaryLabel={ Strings.continue }
					secondaryLabel={ Strings.cancel }
					message={ Strings.register_tenant_error }
					onConfirm={ this.onRegistration }
					onCancel={ this.onHide }/> }
			</div>
		</PageContent>;
	}

	private onValidateProperty = () => {
		showLoading();
		const { property } = this.context;
		validateProperty( property! ).then( ( resp: { property: EjariProperty, AllowedCoTenants?: number } ) => {
			PropertyDetails.updateProperty( resp!.property );
			updateTenantContext( { ...this.context, AllowedCoTenants: resp.AllowedCoTenants } );
			this.onVerifyTenantUserProfile();
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	private onVerifyTenantUserProfile() {
		verifyTenantUserProfile().then( ( tenantId: number | undefined ) => {
			hideLoading();
			if ( tenantId && tenantId !== 0 ) {
				this.startTenantContact();
			} else {
				this.setState( { showConfirmation: true } );
			}
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private startTenantContact() {
		const { property, AllowedCoTenants } = this.context;
		const data = { property: property, action: TenancyContactAction.NEW_CONTRACT, AllowedCoTenants: AllowedCoTenants };
		resetTenantContractContext();
		updateTenantContractContext( data );
		navigateWithInParent( "tenant", "contract/new" );
	}

	private onRegistration = () => {
		navigateWithInParent( "tenant", "registration" );
	};

	private onHide = () => {
		this.setState( {
			showConfirmation: false,
		} );
	};
}

export function validateProperty( property: EjariProperty, notAnErrorCodes: ( string | number ) [] = [] ): Promise<{ property: EjariProperty, AllowedCoTenants?: number }> {
	let ejariPropertyID = getEjariPropertyId( property );
	let propertyID = getPropertyId( property );
	if ( ejariPropertyID && !isEmptyStringOrNull( ejariPropertyID ) ) {
		propertyID = 0;
	} else if ( propertyID ) {
		ejariPropertyID = "0";
	}
	return EjariApi.validateTenantProperty( ejariPropertyID!, propertyID!, property!.PropertyTypeId,
		property!.IsFreeHold, notAnErrorCodes ).then( ( resp: { property: EjariProperty, AllowedCoTenants: number } | null ) => {
		if ( resp !== null )
			setPropertyId( property!, resp.property.EjariPropertyId );
		return Promise.resolve( { property: property, AllowedCoTenants: resp?.AllowedCoTenants } );
	} ).catch( e => {
		return Promise.reject( e );
	} );
}

export function verifyTenantUserProfile(): Promise<number | undefined> {
	const userState = getAppServices().storeService.getStore().getState().User;
	if ( userState && userState.tenantId && userState.tenantId !== 0 ) {
		return Promise.resolve( userState.tenantId );
	} else {
		const eid = SSO.getEmiratesID();
		const req: VerifyTentUserProfile = {
			EmiratesId: eid ? eid : "",
			ActiveIntegrationUserTypeId: 2,
			ActiveUserID: 0,
		};
		return EjariApi.verifyTenantUserProfile( req ).then( ( resp: TenantUserProfile ) => {
			const tenantId = resp.ParticipantId;
			if ( tenantId && tenantId !== 0 ) {
				dispatch( Actions.UPDATE_TENANT_ID, tenantId );
				return Promise.resolve( tenantId );
			} else if ( resp.ValidationError && resp.ValidationError.length > 0 ) {
				const error = resp.ValidationError[ 0 ];
				if ( error === 25 || error === 26 ) {
					return Promise.reject( Strings.profile_found );
				}
			}
			return Promise.resolve( undefined );
		} ).catch( e => {
			return Promise.reject( e );
		} );
	}
}

export default PropertyDetails;
